<template>
  <div class="dashboard">
    <ViewpointsInviteDialog ref="viewpointsInviteDialog" data-test="md-invite-modal" />
    <AdHocReportPicker
      ref="adHocReportsPicker"
      :shown="isAdHocReportPickerShown"
      @close="hideAdHocReportPicker"
      data-test="md-ad-hoc-reports-picker"
    />
    <Modal
      v-model="isOutcomeModalDisplayed"
      title="Change Pathway?"
      ok-text="Yes"
      cancel-text="Cancel"
      cancel-type="primary"
      ok-type="default"
      @hide="handleChangeOutcomeHide"
      size="sm"
      data-test="md-change-outcome-modal"
    >
      <template #title>
        <h2 class="text-center">Change Pathway?</h2>
      </template>
      <LoadingSpinner
        v-if="isSoloReportPathwaySelectionLoading"
        data-test="change-outcome-loading"
      />
      <p v-else-if="soloReportPathwayError" class="text-danger" data-test="change-outcome-error">
        Oops, something went wrong. Please try again.
      </p>
      <p v-else-if="isSelectingSoloReportPathway" data-test="selecting-solo-report-pathway-msg">
        Selecting this pathway will replace your current pathway and your progress will not be
        saved. Would you still like to initiate this pathway?
      </p>
      <div v-else data-test="change-outcome-msg">
        <p>Oh no! If this pathway doesn't suit your current development then you can change it.</p>
        <p>
          Changing your Pathway will affect your current progress. Click ‘Yes’ and you will be
          prompted to pick another Pathway.
        </p>
      </div>
      <template #footer v-if="isSelectingSoloReportPathway">
        <button
          class="btn btn-primary"
          @click="isOutcomeModalDisplayed = false"
          data-test="close-change-outcome-btn"
          type="button"
        >
          Cancel
        </button>
        <button
          class="btn btn-default"
          :disabled="isChangeOutcomeModalOkButtonDisabled"
          @click="handleChangeOutcomeHide('ok')"
          data-test="select-solo-report-pathway-btn"
          type="button"
        >
          Yes
        </button>
      </template>
    </Modal>
    <section class="dashboard-background">
      <div class="container maxWidth-md text-big">
        <div class="row">
          <!-- OUTCOME -->
          <div class="col-sm-6 left-column">
            <DashboardOutcomeTile
              ref="outcomeTile"
              @openKebab="onKebabOpen"
              @update:isChangeOutcomeModalDisplayed="handleChangeOutcomeModalShow(false)"
            />
          </div>
          <!-- END OF OUTCOME -->

          <div class="col-sm-6 right-column">
            <div class="row top-row">
              <!-- SOLO -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel
                  href="/soloreport/"
                  :is-clickable="isSoloHeadLoaded"
                  title="Solo"
                  tooltip-text="My Solo report shows me how I see myself"
                  data-test="md-solo-tile"
                >
                  <TileKebabMenu
                    ref="soloTileKebab"
                    data-test="solo-kebab-menu"
                    @openKebab="onKebabOpen"
                  >
                    <li data-test="solo-menu-item-0">
                      <a href="/soloreport/">View Solo Report</a>
                    </li>
                    <li data-test="solo-menu-item-1">
                      <a href="/resources/session/gyreinsights-your-solo-report"
                        ><span>GyreInsights: Solo report</span>
                        <span class="beta-sticker">BETA</span></a
                      >
                    </li>
                    <li data-test="solo-menu-item-2">
                      <a
                        role="button"
                        tabindex="0"
                        style="padding: 0"
                        aria-label="review yourself again"
                      >
                        <form action="/review/reviewyourselfagain" method="post">
                          <input
                            type="submit"
                            value="Create new solo report"
                            class="repeat-strengths-mapping-input"
                            data-test="repeat-solo-link"
                          />
                        </form>
                      </a>
                    </li>
                  </TileKebabMenu>
                  <div :class="{ hidden: !isSoloHeadLoaded }" class="panel-head-container">
                    <HeadFromModel
                      :animate-entrance="true"
                      :head-id="userId"
                      kind="solo"
                      @onDataLoaded="soloHeadLoaded"
                      data-test="md-solo-head"
                    />
                  </div>
                </DashboardPanel>
              </div>
              <!-- END OF SOLO -->

              <!-- VIEWPOINTS -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel
                  :href="hasViewpointsReport ? '/viewpoints/' : '/my/pathway/viewpoints-invite'"
                  title="Viewpoints"
                  tooltip-text="My Viewpoints report shows how others view me"
                  :is-clickable="isViewpointsHeadLoaded"
                  data-test="md-vp-tile"
                >
                  <TileKebabMenu
                    ref="viewpointsTileKebab"
                    data-test="viewpoints-kebab-menu"
                    @openKebab="onKebabOpen"
                  >
                    <li v-if="hasViewpointsReport" data-test="viewpoints-menu-item-0">
                      <a href="/viewpoints/">View Viewpoints Report</a>
                    </li>
                    <li v-if="hasViewpointsReport" data-test="viewpoints-menu-item-1">
                      <a href="/resources/session/gyreinsights-your-viewpoints-report"
                        ><span>GyreInsights: Viewpoints report</span>
                        <span class="beta-sticker">BETA</span></a
                      >
                    </li>
                    <li data-test="viewpoints-menu-item-2">
                      <a href="javascript:void(0)" @click="openViewpointsInviteDialog"
                        >Invite people to review me</a
                      >
                    </li>
                    <li data-test="viewpoints-menu-item-3">
                      <a href="/my/pathway/viewpoints-invite"> Viewpoints pathway </a>
                    </li>
                    <li v-if="hasViewpointsReport" data-test="viewpoints-menu-item-4">
                      <a href="/my/pathway/working-with-your-viewpoints-report">
                        Working with my Viewpoints Report pathway
                      </a>
                    </li>
                  </TileKebabMenu>
                  <div
                    v-if="isViewpointsHeadLoaded && !hasViewpointsReport"
                    class="no-viewpoints-container"
                    data-test="no-viewpoints-content"
                  >
                    <p>
                      You need at least two Viewpoints reports before you can view your Viewpoints
                      report.
                    </p>
                    <a
                      href="/my/pathway/viewpoints-invite"
                      class="btn btn-default"
                      data-test="viewpoints-pathway-btn"
                      >Viewpoints pathway</a
                    >
                  </div>
                  <div
                    v-else-if="!isViewpointsHeadLoaded || hasViewpointsReport"
                    :class="{ hidden: !isViewpointsHeadLoaded || !hasViewpointsReport }"
                    class="panel-head-container"
                  >
                    <HeadFromModel
                      :animateEntrance="true"
                      :headId="userId"
                      kind="viewpoints"
                      @onDataLoaded="viewpointsHeadLoaded"
                      data-test="md-viewpoints-head"
                    />
                  </div>
                </DashboardPanel>
              </div>
              <!-- END OF VIEWPOINTS -->
            </div>

            <div class="row bottom-row">
              <!-- REPORTS -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel title="My Reports" href="/my/reports" data-test="md-reports-tile">
                  <TileKebabMenu
                    v-if="isInBeta"
                    ref="myReportsTileKebab"
                    data-test="my-reports-kebab-menu"
                    @openKebab="onKebabOpen"
                  >
                    <li data-test="my-reports-menu-item-0">
                      <a href="javascript:void(0)" @click.prevent="handleAdhocReportClick()"
                        >Create Ad Hoc Report...</a
                      >
                    </li>
                  </TileKebabMenu>
                  <img
                    alt="My Reports"
                    src="../../assets/dashboards/my-reports-icon.png"
                    class="my-reports-panel-image"
                    data-test="my-reports-panel-image"
                  />
                </DashboardPanel>
              </div>
              <!-- END OF REPORTS -->

              <!-- DEVELOP -->
              <div class="col-sm-6 panel-container">
                <DashboardPanel
                  title="Develop"
                  tooltip-text="Choose one of Gyre’s 34 Strengths to develop over a 25-day plan"
                  data-test="md-develop-tile"
                  :href="
                    !!userProfile && !!userProfile.developProgress && lastCompletedDayIndex < 25
                      ? '/develop'
                      : '/resources/playlists/the-34-strengths'
                  "
                >
                  <div
                    v-if="userProfile && userProfile.developProgress"
                    data-test="develop-tile-content"
                    class="develop-container"
                  >
                    <span class="develop-strength" data-test="develop-tile-strength">{{
                      userProfile.developProgress?.currentStrengthProgress.strengthItemDisplayTitle
                    }}</span
                    ><span v-if="lastCompletedDayIndex > 24">
                      <span class="develop-strength">:</span><br /><strong
                        data-test="develop-tile-complete"
                        >Complete 🎉</strong
                      >
                    </span>

                    <DevelopProgress
                      :model="userProfile.developProgress"
                      class="develop-progress"
                    />
                    <span
                      v-if="lastCompletedDayIndex < 25"
                      data-test="develop-tile-day"
                      class="develop-day-of-25"
                      >Day
                      {{ lastCompletedDayIndex + 1 }}
                      of 25</span
                    >
                    <button
                      v-else
                      data-test="develop-tile-button"
                      class="btn btn-lg btn-default choose-new-plan-btn"
                    >
                      Choose a new plan
                    </button>
                  </div>
                  <div v-else data-test="develop-tile-content" class="begin-develop-container">
                    <p>Once you begin your Develop plan, progress will be shown here.</p>
                    <button data-test="develop-tile-button" class="btn btn-lg btn-default">
                      Choose a plan
                    </button>
                  </div>
                </DashboardPanel>
              </div>
              <!-- END OF DEVELOP -->
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { debug as d } from "debug";
import { Modal } from "uiv";
import DashboardPanel from "../../components/dashboard-panel.vue";
import DashboardOutcomeTile from "../../components/dashboard-tiles/dashboard-outcome-tile.vue";
import DevelopProgress from "../../components/develop-progress.vue";
import HeadFromModel from "../../components/head/head-from-model.vue";
import { csContext } from "../../services/cs-context";
import TileKebabMenu from "../../components/tile-kebab-menu.vue";
import cms from "../../services/cms/cms";
import { CmsPersonalPathway } from "../../services/cms/cms-type-definitions";
import useSelectPersonalPathway from "../../composables/useSelectPersonalPathway";
import LoadingSpinner from "../../components/loading-spinner.vue";
import { InviteControlApiModel, ProfileApiModel } from "../../api-models";
import { useAxios } from "../../composables/api";
import ViewpointsInviteDialog from "../../components/invite-control/viewpoints-invite-dialog.vue";
import AdHocReportPicker from "../../components/my-reports/ad-hoc-report-picker.vue";
import uiConfig from "../../ui-config";

const debug = d("cs.MyDashboard");

const outcomeTile = ref();
const soloTileKebab = ref();
const viewpointsTileKebab = ref();
const userId = ref(csContext.user.id);
const userProfile = ref<ProfileApiModel>(csContext.userProfile);
const isSoloHeadLoaded = ref(false);
const isOutcomeModalDisplayed = ref(false);
const isSelectingSoloReportPathway = ref(false);
const soloReportPathway = ref<CmsPersonalPathway | null>(null);
const isSoloReportPathwaySelectionLoading = ref(false);
const soloReportPathwayError = ref(false);
const isViewpointsHeadLoaded = ref(false);
const hasViewpointsReport = ref(false);
const viewpointsInviteDialog = ref<InstanceType<typeof ViewpointsInviteDialog>>();
const isAdHocReportPickerShown = ref(false);
// this is a flag to hide the adhoc report creation menu item until the adhoc reports are ready for production
const { isInBeta } = uiConfig;

const { data: inviteControlModel, promise: inviteControlModelPromise } =
  useAxios<InviteControlApiModel>("/InviteCodes/ViewpointsReviewInviteModel");

const onKebabOpen = () => {
  if (outcomeTile.value.outcomeTileKebab) {
    outcomeTile.value.outcomeTileKebab.close();
  }
  soloTileKebab.value.close();
  viewpointsTileKebab.value?.close();
};

const lastCompletedDayIndex = computed(() =>
  userProfile.value?.developProgress
    ? userProfile.value.developProgress.currentStrengthProgress.lastCompletedDayIndex
    : undefined,
);

function soloHeadLoaded(hasData: boolean): void {
  debug("soloDiamondLoaded", hasData);
  if (!hasData) return;
  isSoloHeadLoaded.value = true;
}

async function getWorkingWithYourSoloReportPathway() {
  const pathwayUid = "working-with-your-solo-report";
  soloReportPathway.value = await cms.getByUid("cms-personal-pathway", pathwayUid);
}

const isChangeOutcomeModalOkButtonDisabled = computed<boolean>(() => {
  return isSelectingSoloReportPathway.value && !soloReportPathway.value;
});

function viewpointsHeadLoaded(hasData: boolean): void {
  debug("viewpointsDiamondLoaded", hasData);
  isViewpointsHeadLoaded.value = true;
  hasViewpointsReport.value = hasData;
}

function showViewpointsInviteDialog(): void {
  if (inviteControlModel.value) {
    viewpointsInviteDialog.value?.open(inviteControlModel.value).finally(() => {
      viewpointsInviteDialog.value?.close();
    });
  }
}

function openViewpointsInviteDialog(): void {
  debug("openViewpointsInviteDialog");
  if (inviteControlModel.value) {
    debug("showing immediately");
    showViewpointsInviteDialog();
  } else {
    debug("deferring show");
    inviteControlModelPromise?.then(() => {
      debug("showing after deferral");
      showViewpointsInviteDialog();
    });
  }
}

const handleChangeOutcomeModalShow = async (
  hasSelectedSoloReportPathway: boolean,
): Promise<void> => {
  isOutcomeModalDisplayed.value = true;
  isSelectingSoloReportPathway.value = hasSelectedSoloReportPathway;

  if (hasSelectedSoloReportPathway) {
    await getWorkingWithYourSoloReportPathway();
  }
};

const selectWorkingWithSoloReportPathway = (): void => {
  if (soloReportPathway.value) {
    const { error, success, isLoading } = useSelectPersonalPathway(soloReportPathway.value);
    isSoloReportPathwaySelectionLoading.value = isLoading.value;
    soloReportPathwayError.value = error.value;

    watch([success], (newValue) => {
      if (newValue) {
        window.location.href = "/my/pathway/working-with-your-solo-report";
      }
    });
  }
};

const handleChangeOutcomeHide = (msg: "ok" | "cancel"): void => {
  if (msg === "ok") {
    if (isSelectingSoloReportPathway.value && soloReportPathway.value) {
      selectWorkingWithSoloReportPathway();
    } else {
      window.location.href = "/my/outcomes/";
    }
  }
};

function handleAdhocReportClick() {
  isAdHocReportPickerShown.value = true;
}

function hideAdHocReportPicker() {
  isAdHocReportPickerShown.value = false;
}
</script>

<style scoped lang="less">
@import "../../styles/dashboards/dashboards.less";
@import (reference) "../../styles/bootstrap/variables.less";

.no-viewpoints-container {
  p {
    margin: 20px 20px 50px;
  }

  .btn {
    font-size: 16px;
  }
}

.develop-strength {
  color: @innovator-color;
  font-weight: 700;
}

.begin-develop-container,
.develop-container {
  p {
    margin: 50px;
  }

  .btn {
    font-size: 16px;
  }
}

.develop-container .btn {
  margin-top: 15px;
}

.develop-container,
.begin-develop-container,
.no-viewpoints-container {
  @media (max-width: @screen-sm-max) {
    p {
      font-size: 14px;
      margin: 10px 0 20px;
    }

    .btn.btn-default {
      font-size: 14px;
    }
  }

  @media (max-width: @screen-xxs-max) {
    p {
      font-size: 12px;
      margin: 5px 0 10px;
    }

    .btn.btn-default {
      font-size: 12px;
    }
  }
}

@media (min-width: @screen-sm) {
  .develop-progress {
    zoom: 0.8;
    -moz-transform: scale(0.8);
  }

  .develop-container .btn {
    margin-top: 0;
  }

  .develop-container,
  .begin-develop-container,
  .no-viewpoints-container {
    font-size: 16px;
  }

  .begin-develop-container,
  .develop-container,
  .no-viewpoints-container {
    p {
      margin: 0 0 20px;
    }

    .btn {
      font-size: 14px;
      padding: 6px 12px;
    }
  }
}

@media (min-width: @screen-sm) and (max-width: @screen-sm-max),
  (max-height: 770px) and (min-width: @screen-sm) {
  .develop-container,
  .begin-develop-container {
    font-size: 14px;
  }
}

// to make sure the input takes up the whole space of the a tag
.repeat-strengths-mapping-input {
  border: 0;
  background: unset;
  padding: 3px 20px;
  display: block;
  width: 100%;
  max-width: 100%;
  text-align: left;
  clear: both;
  margin: 0;
}
</style>
<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";
// BUG FIX FOR DEVELOP TILE STYLES ON SMALL SCREENS
@media (max-width: @screen-xs-max) {
  .develop-day-of-25 {
    font-size: 14px;
  }

  .develop-progress {
    width: 100px;
    transform: scale(0.5);
  }

  .review-scores {
    margin: 0 !important;
    font-size: 12px !important;
  }

  .choose-new-plan-btn {
    display: none !important;
  }
}
</style>
